import { Marker, InfoWindow } from '@react-google-maps/api';

// Components
import Icon from './icon';
// Utils
import { convertEntities } from '../utils/helpers';

/**
 * The InfoWindowContent component renders the content displayed within an informational
 * window on a map.
 *
 * @param {Object} props         Properties passed to the component.
 * @param {Object} props.address An object containing address details.
 * @param {Object} props.content Content data for the info window.
 *
 * @return {JSX.Element} A JSX element representing the info window content structure.
 */
const InfoWindowContent = ( { address, content } ) => {
	const { email, phone, website, title } = content;
	const currentAddress = address?.address;

	return (
		<div className="infowindow__content">
			<h3 className="infowindow__title">{ convertEntities( title ) }</h3>
			<ul className="infowindow__data">
				{ currentAddress && ( <li className="infowindow__data-item"><Icon name="map-pin" />{ currentAddress }</li> ) }
				{ phone && ( <li className="infowindow__data-item"><Icon name="phone" />{ phone }</li> ) }
				{ email && ( <li className="infowindow__data-item"><Icon name="email" /><a href={ `mailto:${ email }` }>{ email }</a></li> ) }
				{ website && ( <li className="infowindow__data-item"><Icon name="globe" /><a href={ website } target="_blank" rel="noopener noreferrer">{ website }</a></li> ) }
			</ul>
		</div>
	);
};

/**
 * The Pin component represents a map marker with custom interactions and an associated
 * info window.
 *
 * @param {Object}   props                   Properties passed to the component.
 * @param {number}   props.index             The index of the marker (likely for identification).
 * @param {Object}   props.data              The data associated with the map marker.
 * @param {Object}   props.clusterer         A reference to a map marker clustering object (if used).
 * @param {Function} props.setSelectedMarker A function to set the currently selected marker.
 * @param {Object}   props.selectedMarker    The data of the currently selected marker.
 * @return {JSX.Element} A JSX element representing the map marker with its behavior.
 */
const Pin = ( { index, data, clusterer, setSelectedMarker, selectedMarker } ) => {
	const { lat, lng } = data;
	const position = { lat, lng };

	const onClick = () => {
		setSelectedMarker( data );
	};

	const renderInfoWindow = () => {
		if ( ! selectedMarker ) {
			return null;
		}

		if ( selectedMarker.id !== data.id ) {
			return null;
		}

		const { lat: currentLat, lng: currentLng, address, content } = selectedMarker;
		const markerPosition = { lat: currentLat, lng: currentLng };

		return (
			<InfoWindow position={ markerPosition } onCloseClick={ () => {
				setSelectedMarker( null );
			} }>
				<InfoWindowContent address={ address } content={ content } />
			</InfoWindow>
		);
	};

	return (
		<Marker
			key={ index }
			clusterer={ clusterer }
			onClick={ onClick }
			position={ position }
			data={ data }
		>
			{ renderInfoWindow() }
		</Marker>
	);
};

Pin.defaultProps = {
	children: null,
	opacity: 1,
	onClickFunc: () => { },
};

export default Pin;
